import { useMemo } from "react";
import { getDatabase, ref } from "firebase/database";
import Firebase from "./Firebase";
import { useListVals } from "./databaseHooks";

const db = getDatabase(Firebase);

export function useMergedListVals(primaryPath, legacyPath, additionalPath) {
	const [primary = [], primaryLoading] = useListVals(ref(db, primaryPath));
	const [legacy = [], legacyLoading] = useListVals(ref(db, legacyPath));
	const [additional = [], additionalLoading] = useListVals(
		additionalPath ? ref(db, additionalPath) : null
	);

	const merged = useMemo(
		() => mergeByRecordId(
			(legacy || []).map((item) => ({ ...item, _sourceCollection: legacyPath })),
			(additional || []).map((item) => ({ ...item, _sourceCollection: additionalPath })),
			(primary || []).map((item) => ({ ...item, _sourceCollection: primaryPath }))
		),
		[primary, legacy, additional, primaryPath, legacyPath, additionalPath]
	);

	return [merged, primaryLoading || legacyLoading || additionalLoading];
}

export function mergeByRecordId(...collections) {
	const merged = new Map();

	collections.flatMap((collection) => collection || []).forEach((item, index) => {
		if (!item || typeof item !== "object") return;
		const key = getRecordId(item) || `index-${index}`;
		merged.set(key, {
			...(merged.get(key) || {}),
			...item,
		});
	});

	return Array.from(merged.values());
}

export function getRecordId(item = {}) {
	return (
		item.id ||
		item.userID ||
		item.surveyID ||
		item.questionID ||
		item.responseID ||
		item.interactionID ||
		item.lessonActivityID ||
		item.priorityID ||
		item.reviewID ||
		item.consentRequestID ||
		item.storyID ||
		item.feedbackID ||
		item.feedbackId ||
		item.orgID ||
		item.communityID ||
		item.languageID ||
		item.initiativeID ||
		item.userRoleID ||
		item.typeID ||
		item.typeId ||
		item.code
	);
}

export function findByAnyId(items = [], id, fields = ["id"]) {
	if (!id) return null;
	return (items || []).find((item) =>
		fields.some((field) => item?.[field] === id)
	) || null;
}

export function getLabel(items, id, fields, labelFields, fallback = "-") {
	const item = findByAnyId(items, id, fields);
	if (!item) return fallback;

	for (const field of labelFields) {
		if (item[field]) return item[field];
	}

	return fallback;
}

export function getUserName(users = [], id, fallback = "-") {
	const user = findByAnyId(users, id, ["id", "userID", "uid"]);
	if (!user) return fallback;
	const fullName = [user.firstname, user.surname].filter(Boolean).join(" ").trim();
	return fullName || user.fullname || user.name || fallback;
}

export function getOrganizationName(organizations = [], id, fallback = "-") {
	if (!id) return fallback;
	const organization = findByAnyId(
		organizations,
		id,
		["id", "orgID", "organisationID"]
	);
	if (!organization) return fallback;
	return organization.name || organization.organisationName || fallback;
}

export function getSubmittedById(item = {}) {
	return (
		item.submittedBy ||
		item.submittedByUserID ||
		item.submitedByuserID ||
		item.addedByUser ||
		item.createdBy ||
		item.registeredByUserID ||
		item.userID ||
		item.id
	);
}

export function getInteractionSubmitterId(item = {}) {
	item = item || {};
	return (
		item.userId ||
		item.submittedBy ||
		item.submittedByUserID ||
		item.submitedByuserID ||
		item.createdBy ||
		item.addedByUser ||
		item.userID ||
		null
	);
}

const INTERACTION_TYPE_LABELS = Object.freeze({
	"visit-meeting": "Visit Or Meeting",
	"taught-lesson": "Taught lesson",
	"whatsapp-text": "Whatsapp Or Text",
	"phone-call": "Phone Call",
});

export function getInteractionTypeLabel(item = {}) {
	item = item || {};
	const rawType = String(
		item.interactionType || item.interaction_type || item.type || ""
	).trim();

	if (!rawType) return "Not specified";
	return INTERACTION_TYPE_LABELS[rawType.toLowerCase()] || rawType;
}

function firstDefined(item, fields, fallback = null) {
	for (const field of fields) {
		if (item?.[field] !== undefined && item?.[field] !== null && item?.[field] !== "") {
			return item[field];
		}
	}
	return fallback;
}

export function getInteractionMetadata(item = {}) {
	item = item || {};
	return {
		interactionId: firstDefined(item, ["interactionID", "id"]),
		lessonId: firstDefined(item, ["lessonId", "lessonID"]),
		organisationId: firstDefined(item, ["organisationID", "orgID", "organizationID", "organisationId"]),
		communityId: firstDefined(item, ["communityID", "communityId"]),
		workerId: getInteractionSubmitterId(item),
		communicatedWithId: firstDefined(item, ["communicatedWithId", "communicatedWithUser", "communicatedWith"]),
		communicatedWithName: firstDefined(item, ["communicatedWithUserName", "communicatedWithOther"]),
		date: firstDefined(item, ["dateTime", "date", "timeStamp", "createdAt"]),
	};
}

export function getInteractionParticipantCounts(item = {}) {
	item = item || {};
	return {
		males: firstDefined(item, ["countMales", "maleCount", "males"], 0),
		females: firstDefined(item, ["countFemales", "femaleCount", "females"], 0),
		youths: firstDefined(item, ["countChildrenYouth", "youthCount", "youth", "youths"], 0),
		disabled: firstDefined(item, ["countDisability", "disabledCount", "disabled"], 0),
		elderly: firstDefined(item, ["countElderly", "elderlyCount", "elderly"], 0),
	};
}

export function normalizeBoolean(value) {
	if (typeof value === "string") {
		const normalized = value.trim().toLowerCase();
		if (["false", "0", "no", "n"].includes(normalized)) return false;
		if (["true", "1", "yes", "y"].includes(normalized)) return true;
	}
	return Boolean(value);
}

export function classifyInteractionMediaName(name = "") {
	const normalized = String(name).toLowerCase();
	if (normalized.includes("participant") || normalized.includes("firstimage")) return "participants";
	if (normalized.includes("attendance") || normalized.includes("secondimage")) return "attendance";
	return "document";
}

export function getInteractionInlineMedia(item = {}) {
	item = item || {};
	const candidates = [
		["participants", item.media?.photoParticipants, "Participants Photo"],
		["attendance", item.media?.photoAttendance, "Attendance Photo"],
		["document", item.media?.photoDocument, "Document"],
		["participants", item.photoParticipants, "Participants Photo"],
		["attendance", item.photoAttendanceList || item.photoAttendance, "Attendance Photo"],
		["document", item.photoDocuments || item.photoDocument, "Document"],
	];
	const seen = new Set();
	return candidates.reduce((result, [kind, url, name]) => {
		const normalizedUrl = String(url || "").trim();
		if (!normalizedUrl || seen.has(normalizedUrl)) return result;
		seen.add(normalizedUrl);
		result.push({ kind, url: normalizedUrl, name });
		return result;
	}, []);
}

export function lower(value) {
	return String(value || "").toLowerCase();
}

export function includesSearch(values, searchText) {
	const needle = lower(searchText);
	if (!needle) return true;
	return values.some((value) => lower(value).includes(needle));
}
